import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";

import { Cancel as CancelIcon, Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";

import { FaChevronDown, FaChevronUp, FaCopy, FaEdit, FaEye, FaPaperclip, FaSave, FaSearch, FaTimes, FaTrash } from "react-icons/fa";

import "./CadastroImoveis.css";
import ThemeProvider from "../theme/TemaMuiProvider";
import muiTheme from "../theme/muiTheme";

/* =========================================================
   CONFIGURAÇÕES
========================================================= */
const API_URL = "/api/ditr";

const ESTADO_INICIAL = {
  id: null,
  ccirId: "",
  codigoDitr: "",
  codigoCcir: "",
  nomeImovelRural: "",
  apelido: "",
  areaTotalHa: "",
  codigoImovelIncra: "",
  endereco: "",
  municipio: "",
  uf: "",
  cep: "",
  observacao: "",
};

const COLUNAS_BUSCA = ["codigoDitr", "codigoCcir", "nomeImovelRural", "apelido", "codigoImovelIncra", "endereco", "municipio", "uf", "cep", "observacao"];

/* =========================================================
   FUNÇÕES AUXILIARES
========================================================= */
function obterToken() {
  return localStorage.getItem("token") || sessionStorage.getItem("token") || "";
}

function criarHeaders(incluirJson = true) {
  const token = obterToken();
  const headers = {};

  if (incluirJson) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function lerResposta(response) {
  const tipo = response.headers.get("content-type") || "";

  if (tipo.includes("application/json")) {
    return response.json();
  }

  const texto = await response.text();
  return texto ? { erro: texto } : {};
}

async function requisicao(url, options = {}) {
  const response = await fetch(url, options);
  const dados = await lerResposta(response);

  if (!response.ok) {
    throw new Error(dados.erro || dados.mensagem || `Erro HTTP ${response.status}.`);
  }

  return dados;
}

function textoMaiusculo(valor) {
  return String(valor ?? "").toUpperCase();
}

function somenteDigitos(valor) {
  return String(valor ?? "").replace(/\D/g, "");
}

function formatarCepDigitacao(valor) {
  const digitos = somenteDigitos(valor).slice(0, 8);

  if (digitos.length <= 5) {
    return digitos;
  }

  return `${digitos.slice(0, 5)}-${digitos.slice(5)}`;
}

function normalizarBusca(valor) {
  return String(valor ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .trim();
}

function formatarArea(valor) {
  if (valor === undefined || valor === null || valor === "") {
    return "-";
  }

  const numero = Number(valor);

  if (!Number.isFinite(numero)) {
    return String(valor);
  }

  return numero.toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });
}

function extrairNomeArquivo(contentDisposition, nomePadrao = "arquivo") {
  if (!contentDisposition) {
    return nomePadrao;
  }

  const utf8 = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);

  if (utf8?.[1]) {
    return decodeURIComponent(utf8[1]);
  }

  const simples = contentDisposition.match(/filename="?([^";]+)"?/i);

  return simples?.[1] || nomePadrao;
}

/* =========================================================
   COMPONENTE PRINCIPAL
========================================================= */
export default function DITR() {
  const [registros, setRegistros] = useState([]);
  const [ccirs, setCcirs] = useState([]);

  const [modalImovelAberto, setModalImovelAberto] = useState(false);
  const [buscaImovel, setBuscaImovel] = useState("");

  const [form, setForm] = useState(ESTADO_INICIAL);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [modoVisualizacao, setModoVisualizacao] = useState(false);

  const [busca, setBusca] = useState("");
  const [pagina, setPagina] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(10);
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const salvo = localStorage.getItem("ditrGridMinimizado");
    return salvo ? salvo === "1" : false;
  });

  const [gedAberto, setGedAberto] = useState(false);
  const [registroGed, setRegistroGed] = useState(null);
  const [arquivosGed, setArquivosGed] = useState([]);
  const [arquivosPendentesCadastro, setArquivosPendentesCadastro] = useState([]);
  const [carregandoGed, setCarregandoGed] = useState(false);
  const [arquivoExcluindo, setArquivoExcluindo] = useState(null);

  const [mensagem, setMensagem] = useState({
    aberta: false,
    texto: "",
    tipo: "success",
  });

  const editando = Boolean(form.id);
  /* =======================================================
     MENSAGENS
  ======================================================= */
  const exibirMensagem = useCallback((texto, tipo = "success") => {
    setMensagem({
      aberta: true,
      texto,
      tipo,
    });
  }, []);

  const fecharMensagem = () => {
    setMensagem((anterior) => ({
      ...anterior,
      aberta: false,
    }));
  };
  /* =======================================================
     CARREGAMENTO
  ======================================================= */
  const carregarRegistros = useCallback(async () => {
    setCarregando(true);

    try {
      const dados = await requisicao(API_URL, {
        headers: criarHeaders(false),
      });

      setRegistros(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar DITR:", error);
      setRegistros([]);
      exibirMensagem(error.message || "Não foi possível carregar os DITRs.", "error");
    } finally {
      setCarregando(false);
    }
  }, [exibirMensagem]);

  const carregarCcirs = useCallback(async () => {
    try {
      const dados = await requisicao(`${API_URL}/ccirs-disponiveis`, {
        headers: criarHeaders(false),
      });

      setCcirs(Array.isArray(dados) ? dados : []);
    } catch (error) {
      console.error("Erro ao carregar CCIRs disponíveis para DITR:", error);

      setCcirs([]);
      exibirMensagem(error.message || "Não foi possível carregar os imóveis rurais do CCIR.", "error");
    }
  }, [exibirMensagem]);

  useEffect(() => {
    carregarRegistros();
    carregarCcirs();
  }, [carregarRegistros, carregarCcirs]);

  useEffect(() => {
    localStorage.setItem("ditrGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  /* =======================================================
     BUSCA E PAGINAÇÃO
  ======================================================= */
  const registrosFiltrados = useMemo(() => {
    const termo = normalizarBusca(busca);

    if (!termo) {
      return registros;
    }

    return registros.filter((registro) => COLUNAS_BUSCA.some((coluna) => normalizarBusca(registro[coluna]).includes(termo)));
  }, [busca, registros]);

  const registrosPaginados = useMemo(() => {
    const inicio = pagina * linhasPorPagina;

    return registrosFiltrados.slice(inicio, inicio + linhasPorPagina);
  }, [pagina, linhasPorPagina, registrosFiltrados]);

  useEffect(() => {
    const ultimaPagina = Math.max(0, Math.ceil(registrosFiltrados.length / linhasPorPagina) - 1);

    if (pagina > ultimaPagina) {
      setPagina(ultimaPagina);
    }
  }, [pagina, linhasPorPagina, registrosFiltrados.length]);

  const limparFiltro = () => {
    setBusca("");
    setPagina(0);
  };

  const copiarTabela = async () => {
    const cabecalho = ["CÓDIGO", "CCIR", "IMÓVEL", "CÓDIGO INCRA", "ÁREA TOTAL", "MUNICÍPIO/UF", "CEP"];

    const linhas = registrosFiltrados.map((registro) => [
      registro.codigoDitr || "",
      registro.codigoCcir || "",
      registro.apelido || registro.nomeImovelRural || "",
      registro.codigoImovelIncra || "",
      formatarArea(registro.areaTotalHa),
      [registro.municipio, registro.uf].filter(Boolean).join("/"),
      registro.cep || "",
    ]);

    const conteudo = [cabecalho, ...linhas].map((linha) => linha.join("\t")).join("\n");

    try {
      await navigator.clipboard.writeText(conteudo);
      exibirMensagem("Registros copiados com sucesso!", "success");
    } catch (error) {
      console.error("Erro ao copiar registros:", error);
      exibirMensagem("Não foi possível copiar os registros.", "error");
    }
  };

  const exportarCSV = () => {
    const escaparCSV = (valor) => {
      const texto = String(valor ?? "");
      return `"${texto.replace(/"/g, '""')}"`;
    };

    const cabecalho = ["CÓDIGO", "CCIR", "IMÓVEL", "APELIDO", "CÓDIGO INCRA", "ÁREA TOTAL", "ENDEREÇO", "MUNICÍPIO", "UF", "CEP", "OBSERVAÇÕES"];

    const linhas = registrosFiltrados.map((registro) => [
      registro.codigoDitr || "",
      registro.codigoCcir || "",
      registro.nomeImovelRural || "",
      registro.apelido || "",
      registro.codigoImovelIncra || "",
      formatarArea(registro.areaTotalHa),
      registro.endereco || "",
      registro.municipio || "",
      registro.uf || "",
      registro.cep || "",
      registro.observacao || "",
    ]);

    const conteudo = [cabecalho, ...linhas].map((linha) => linha.map(escaparCSV).join(";")).join("\n");

    const blob = new Blob([`\uFEFF${conteudo}`], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;
    link.download = "ditr.csv";

    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  };
  /* =======================================================
     FORMULÁRIO
  ======================================================= */
  const preencherFormularioRegistro = (registro) => ({
    id: registro.id,
    ccirId: registro.ccirId ?? "",
    codigoDitr: registro.codigoDitr ?? "",
    codigoCcir: registro.codigoCcir ?? "",
    nomeImovelRural: registro.nomeImovelRural ?? "",
    apelido: registro.apelido ?? "",
    areaTotalHa: registro.areaTotalHa ?? "",
    codigoImovelIncra: registro.codigoImovelIncra ?? "",
    endereco: registro.endereco ?? "",
    municipio: registro.municipio ?? "",
    uf: registro.uf ?? "",
    cep: formatarCepDigitacao(registro.cep ?? ""),
    observacao: registro.observacao ?? "",
  });

  const abrirEdicao = async (registro) => {
    setArquivosPendentesCadastro([]);
    setForm(preencherFormularioRegistro(registro));
    setModoVisualizacao(false);
    await carregarCcirs();
  };

  const abrirVisualizacao = (registro) => {
    setArquivosPendentesCadastro([]);
    setForm(preencherFormularioRegistro(registro));
    setModoVisualizacao(true);
  };

  const fecharFormulario = () => {
    if (salvando) {
      return;
    }
    setModoVisualizacao(false);
    setForm(ESTADO_INICIAL);
    setArquivosPendentesCadastro([]);
    setBuscaImovel("");
  };

  const alterarCampo = (campo) => (evento) => {
    const valor = evento.target.value;

    setForm((anterior) => ({
      ...anterior,
      [campo]: textoMaiusculo(valor),
    }));
  };

  const alterarCep = (evento) => {
    const valor = formatarCepDigitacao(evento.target.value);

    setForm((anterior) => ({
      ...anterior,
      cep: valor,
    }));
  };

  const abrirModalImovel = () => {
    if (modoVisualizacao) {
      return;
    }

    setBuscaImovel("");
    setModalImovelAberto(true);
  };

  const fecharModalImovel = () => {
    setModalImovelAberto(false);
    setBuscaImovel("");
  };

  const selecionarImovel = (ccir) => {
    if (!ccir) {
      return;
    }

    setForm((anterior) => ({
      ...anterior,

      ccirId: ccir.ccirId ?? ccir.id ?? "",
      codigoCcir: ccir.codigoCcir ?? "",
      nomeImovelRural: ccir.nomeImovelRural ?? "",
      apelido: ccir.apelido ?? "",
      areaTotalHa: ccir.areaTotalHa ?? "",

      codigoImovelIncra: "",
      municipio: "",
      uf: "",
    }));

    setModalImovelAberto(false);
    setBuscaImovel("");
  };

  const validarFormulario = () => {
    if (!form.ccirId) {
      return "Selecione um imóvel rural cadastrado no CCIR.";
    }

    if (!String(form.codigoImovelIncra).trim()) {
      return "Informe o Código do Imóvel no INCRA.";
    }

    if (!String(form.endereco).trim()) {
      return "Informe o Endereço.";
    }

    if (!String(form.municipio).trim()) {
      return "Informe o Município.";
    }

    if (!/^[A-Z]{2}$/.test(String(form.uf).trim())) {
      return "Informe uma UF válida com duas letras.";
    }

    if (somenteDigitos(form.cep).length !== 8) {
      return "Informe um CEP válido com oito números.";
    }

    return null;
  };

  const salvar = async () => {
    const erro = validarFormulario();

    if (erro) {
      exibirMensagem(erro, "warning");
      return;
    }

    const payload = {
      ccirId: Number(form.ccirId),
      codigoImovelIncra: textoMaiusculo(form.codigoImovelIncra).trim(),
      endereco: textoMaiusculo(form.endereco).trim(),
      municipio: textoMaiusculo(form.municipio).trim(),
      uf: textoMaiusculo(form.uf).trim(),
      cep: somenteDigitos(form.cep),
      observacao: textoMaiusculo(form.observacao).trim(),
    };

    setSalvando(true);

    try {
      const url = editando ? `${API_URL}/${form.id}` : API_URL;
      const metodo = editando ? "PUT" : "POST";

      const resposta = await requisicao(url, {
        method: metodo,
        headers: criarHeaders(true),
        body: JSON.stringify(payload),
      });

      let ditrIdSalvo = form.id;

      if (!editando) {
        ditrIdSalvo = resposta.id || resposta.ditrId || resposta.ditr?.id || resposta.registro?.id || resposta.dados?.id;
      }

      if (ditrIdSalvo && arquivosPendentesCadastro.length > 0) {
        await enviarGedPendente(ditrIdSalvo, arquivosPendentesCadastro);
      }

      if (arquivosPendentesCadastro.length > 0 && !ditrIdSalvo) {
        throw new Error("O DITR foi salvo, mas o identificador não foi retornado para enviar o GED.");
      }

      exibirMensagem(resposta.mensagem || (editando ? "DITR atualizado com sucesso!" : "DITR cadastrado com sucesso!"), "success");

      setModoVisualizacao(false);
      setForm(ESTADO_INICIAL);
      setArquivosPendentesCadastro([]);
      setBuscaImovel("");

      await Promise.all([carregarRegistros(), carregarCcirs()]);
    } catch (error) {
      console.error("Erro ao salvar DITR:", error);
      exibirMensagem(error.message || "Não foi possível salvar o DITR.", "error");
    } finally {
      setSalvando(false);
    }
  };

  const selecionarArquivosCadastro = (evento) => {
    const novosArquivos = Array.from(evento.target.files || []);

    if (!novosArquivos.length) {
      return;
    }

    setArquivosPendentesCadastro((anteriores) => [...anteriores, ...novosArquivos]);
    evento.target.value = "";
  };

  const removerArquivoCadastro = (indiceRemover) => {
    setArquivosPendentesCadastro((anteriores) => anteriores.filter((_, indice) => indice !== indiceRemover));
  };

  const enviarGedPendente = async (ditrId, arquivos) => {
    if (!ditrId || !arquivos?.length) {
      return;
    }

    const formData = new FormData();

    arquivos.forEach((arquivo) => {
      formData.append("files", arquivo);
    });

    const token = obterToken();
    const headers = {};

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    await requisicao(`${API_URL}/${ditrId}/ged`, {
      method: "POST",
      headers,
      body: formData,
    });
  };

  /* =======================================================
     EXCLUSÃO
  ======================================================= */
  const excluirRegistro = async (registro) => {
    if (!registro?.id) {
      exibirMensagem("Não foi possível localizar o DITR selecionado.", "error");
      return;
    }

    const confirmar = window.confirm(`Tem certeza que deseja excluir o registro ${registro.codigoDitr || registro.id}?`);

    if (!confirmar) {
      return;
    }

    setExcluindo(true);

    try {
      const resposta = await requisicao(`${API_URL}/${registro.id}`, {
        method: "DELETE",
        headers: criarHeaders(false),
      });

      exibirMensagem(resposta.mensagem || "DITR excluído com sucesso!", "success");

      if (Number(form.id) === Number(registro.id)) {
        fecharFormulario();
      }

      await Promise.all([carregarRegistros(), carregarCcirs()]);
    } catch (error) {
      console.error("Erro ao excluir DITR:", error);
      exibirMensagem(error.message || "Não foi possível excluir o DITR.", "error");
    } finally {
      setExcluindo(false);
    }
  };
  /* =======================================================
     GED
  ======================================================= */
  const carregarArquivosGed = useCallback(
    async (ditrId) => {
      if (!ditrId) {
        return;
      }
      setCarregandoGed(true);

      try {
        const dados = await requisicao(`${API_URL}/${ditrId}/ged`, {
          headers: criarHeaders(false),
        });

        setArquivosGed(Array.isArray(dados) ? dados : []);
      } catch (error) {
        console.error("Erro ao carregar GED do DITR:", error);
        setArquivosGed([]);
        exibirMensagem(error.message || "Não foi possível carregar os documentos.", "error");
      } finally {
        setCarregandoGed(false);
      }
    },
    [exibirMensagem],
  );

  const abrirGed = async (registro) => {
    if (!registro?.id) {
      return;
    }

    setRegistroGed(registro);
    setArquivosGed([]);
    setGedAberto(true);
    await carregarArquivosGed(registro.id);
  };

  const fecharGed = () => {
    if (arquivoExcluindo) {
      return;
    }

    setGedAberto(false);
    setRegistroGed(null);
    setArquivosGed([]);
  };

  const abrirArquivo = async (arquivo) => {
    if (!registroGed?.id || !arquivo?.id) {
      return;
    }

    const extensao = String(arquivo.nome || "")
      .split(".")
      .pop()
      .toLowerCase();

    const visualizaveis = ["pdf", "png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "txt"];

    if (!visualizaveis.includes(extensao)) {
      const confirmar = window.confirm(
        `Visualização indisponível.\n\n` + `O arquivo "${arquivo.nome}" não possui visualização integrada no ImobGest.\n\n` + `Deseja baixar agora?`,
      );

      if (confirmar) {
        await baixarArquivo(arquivo);
      }

      return;
    }

    try {
      const token = obterToken();
      const response = await fetch(`${API_URL}/${registroGed.id}/ged/${arquivo.id}/visualizar`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const dados = await lerResposta(response);
        throw new Error(dados.erro || "Erro ao visualizar o documento.");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      window.open(url, "_blank", "noopener,noreferrer");

      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      console.error("Erro ao visualizar documento:", error);
      exibirMensagem(error.message || "Não foi possível visualizar o documento.", "error");
    }
  };

  const baixarArquivo = async (arquivo) => {
    if (!registroGed?.id || !arquivo?.id) {
      return;
    }

    try {
      const token = obterToken();
      const response = await fetch(`${API_URL}/${registroGed.id}/ged/${arquivo.id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!response.ok) {
        const dados = await lerResposta(response);
        throw new Error(dados.erro || "Erro ao baixar o documento.");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const nome = extrairNomeArquivo(response.headers.get("content-disposition"), arquivo.nome || "documento");

      const link = document.createElement("a");
      link.href = url;
      link.download = nome;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Erro ao baixar documento:", error);
      exibirMensagem(error.message || "Não foi possível baixar o documento.", "error");
    }
  };

  const excluirArquivo = async (arquivo) => {
    if (!registroGed?.id || !arquivo?.id) {
      return;
    }

    const confirmou = window.confirm(`Deseja realmente excluir o arquivo "${arquivo.nome}"?`);

    if (!confirmou) {
      return;
    }

    setArquivoExcluindo(arquivo.id);

    try {
      const resposta = await requisicao(`${API_URL}/${registroGed.id}/ged/${arquivo.id}`, {
        method: "DELETE",
        headers: criarHeaders(false),
      });

      exibirMensagem(resposta.mensagem || "Arquivo excluído com sucesso!", "success");

      await Promise.all([carregarArquivosGed(registroGed.id), carregarRegistros()]);
    } catch (error) {
      console.error("Erro ao excluir documento:", error);
      exibirMensagem(error.message || "Não foi possível excluir o documento.", "error");
    } finally {
      setArquivoExcluindo(null);
    }
  };
  /* =======================================================
     OPÇÕES DO CCIR
  ======================================================= */
  const ccirsSelecionaveis = useMemo(() => {
    return ccirs.filter((ccir) => !ccir.possuiDitr || String(ccir.ditrId) === String(form.id));
  }, [ccirs, form.id]);

  const imoveisFiltrados = useMemo(() => {
    const termo = normalizarBusca(buscaImovel);

    if (!termo) {
      return ccirsSelecionaveis;
    }

    return ccirsSelecionaveis.filter((ccir) => {
      const conteudo = [ccir.codigoCcir, ccir.nomeImovelRural, ccir.apelido, ccir.codigoImovelRural, ccir.municipioSedeImovelRural, ccir.uf]
        .filter(Boolean)
        .join(" ");

      return normalizarBusca(conteudo).includes(termo);
    });
  }, [ccirsSelecionaveis, buscaImovel]);
  /* =======================================================
     RENDERIZAÇÃO
  ======================================================= */
  return (
    <ThemeProvider theme={muiTheme}>
      <div className="local-container">
        <h2>Declaração do Imposto sobre a Propriedade Territorial Rural - DITR</h2>

        <Box
          className="bloco-linha"
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) auto",
            alignItems: "center",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              height: 52,
              width: "100%",
              border: "1px solid var(--ig-b-555555)",
              borderRadius: "4px",
              display: "flex",
              alignItems: "center",
              backgroundColor: "transparent",
              boxSizing: "border-box",
              px: 2,
            }}
          >
            <input
              type="text"
              placeholder="Filtrar registros do DITR"
              value={busca}
              onChange={(evento) => {
                setBusca(evento.target.value);
                setPagina(0);
              }}
              style={{
                flex: 1,
                height: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--ig-t-ffffff)",
                fontSize: "1rem",
              }}
            />

            <SearchIcon
              sx={{
                color: "var(--ig-t-9ca3af)",
                fontSize: 20,
                flexShrink: 0,
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Button size="small" variant="outlined" type="button" onClick={limparFiltro} sx={botaoOutlinedAzulSx}>
              Limpar Filtro
            </Button>

            <Button size="small" variant="contained" type="button" onClick={copiarTabela} sx={botaoAzulSx}>
              <FaCopy style={{ marginRight: 6, fontSize: 16 }} />
              Copiar
            </Button>

            <Button size="small" variant="contained" type="button" onClick={exportarCSV} sx={botaoAzulSx}>
              CSV
            </Button>
          </Box>
        </Box>

        <div className="grid-toggle-area right">
          {!gridMinimizado ? (
            <button type="button" className="btn-grid-toggle small danger" onClick={() => setGridMinimizado(true)}>
              <FaChevronDown />
              Minimizar
            </button>
          ) : (
            <button type="button" className="btn-grid-toggle small outline" onClick={() => setGridMinimizado(false)}>
              <FaChevronUp />
              Expandir
            </button>
          )}
        </div>

        {!gridMinimizado && (
          <div className="bloco-lista">
            {carregando ? (
              <p>Carregando...</p>
            ) : registrosPaginados.length === 0 ? (
              <p>{busca ? "Nenhum DITR encontrado para a busca informada." : "Não há registros para exibir"}</p>
            ) : (
              <>
                <table className="tabela-matriculas" style={{ width: "100%", tableLayout: "fixed" }}>
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "11%" }} />
                    <col style={{ width: "18%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "12%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "18%" }} />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Código</th>
                      <th>CCIR</th>
                      <th>Imóvel</th>
                      <th>Código INCRA</th>
                      <th>Área Total</th>
                      <th>Município/UF</th>
                      <th>Ações</th>
                    </tr>
                  </thead>

                  <tbody>
                    {registrosPaginados.map((registro) => (
                      <tr key={registro.id}>
                        <td>{registro.codigoDitr || "-"}</td>
                        <td>{registro.codigoCcir || "-"}</td>
                        <td>{registro.apelido || registro.nomeImovelRural || "-"}</td>
                        <td>{registro.codigoImovelIncra || "-"}</td>
                        <td>{formatarArea(registro.areaTotalHa)} ha</td>
                        <td>{[registro.municipio, registro.uf].filter(Boolean).join("/") || "-"}</td>
                        <td className="col-acoes">
                          <button
                            type="button"
                            className={`btn-ged-lista ${Number(registro.qtdArquivos) > 0 ? "btn-ged-ok" : ""}`}
                            onClick={(evento) => {
                              evento.preventDefault();
                              evento.stopPropagation();
                              abrirGed(registro);
                            }}
                          >
                            GED
                            {Number(registro.qtdArquivos) > 0 && <span className="ged-badge">{registro.qtdArquivos}</span>}
                          </button>

                          <button
                            type="button"
                            className="btn-editar"
                            onClick={(evento) => {
                              evento.preventDefault();
                              evento.stopPropagation();
                              abrirVisualizacao(registro);
                            }}
                          >
                            <FaEye />
                            Visualizar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "flex-end",
                    pt: 1,
                    overflow: "hidden",
                  }}
                >
                  <TablePagination
                    component="div"
                    count={registrosFiltrados.length}
                    page={pagina}
                    onPageChange={(_, novaPagina) => setPagina(novaPagina)}
                    rowsPerPage={linhasPorPagina}
                    onRowsPerPageChange={(evento) => {
                      setLinhasPorPagina(Number(evento.target.value));
                      setPagina(0);
                    }}
                    rowsPerPageOptions={[5, 10, 25]}
                    labelRowsPerPage="Registros por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                    SelectProps={{ MenuProps: menuPropsDark }}
                    sx={paginacaoSx}
                  />
                </Box>
              </>
            )}
          </div>
        )}

        {gedAberto && (
          <div className="modal-ged-overlay">
            <div className="modal-ged">
              <h3>Arquivos GED - DITR #{registroGed?.codigoDitr || registroGed?.id || ""}</h3>

              {carregandoGed ? (
                <p>Carregando arquivos...</p>
              ) : arquivosGed.length === 0 ? (
                <p>Nenhum arquivo cadastrado para este DITR.</p>
              ) : (
                <ul
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {arquivosGed.map((arquivo) => (
                    <li
                      key={arquivo.id || arquivo.nome}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span style={{ flex: 1 }}>{arquivo.nome}</span>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <button type="button" className="btn-visualizar-ged" onClick={() => abrirArquivo(arquivo)}>
                          <FaEye />
                          Visualizar
                        </button>

                        <button type="button" className="btn-baixar-ged" onClick={() => baixarArquivo(arquivo)}>
                          Baixar
                        </button>

                        <button type="button" className="btn-excluir-ged" onClick={() => excluirArquivo(arquivo)} disabled={arquivoExcluindo === arquivo.id}>
                          <FaTrash />
                          {arquivoExcluindo === arquivo.id ? "Excluindo..." : "Excluir"}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <button type="button" className="btn-fechar-ged" onClick={fecharGed} disabled={Boolean(arquivoExcluindo)}>
                Fechar
              </button>
            </div>
          </div>
        )}

        <div className="bloco-formulario">
          <form
            className="form-grid"
            onSubmit={(evento) => {
              evento.preventDefault();
              evento.stopPropagation();

              if (modoVisualizacao || salvando) {
                return;
              }

              salvar();
            }}
          >
            <Box
              sx={{
                pb: 1,
                borderBottom: "1px solid var(--ig-b-255-255-255-a018)",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: "var(--ig-t-ffffff)",
                  fontSize: "1.05rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Dados do Imóvel Rural
              </h3>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) 320px",
                },
                gap: 2,
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Buscar Imóvel Rural*"
                value={form.nomeImovelRural && form.codigoCcir ? `${form.nomeImovelRural} - ${form.codigoCcir}` : form.nomeImovelRural || ""}
                placeholder="Clique para buscar um imóvel rural"
                onClick={abrirModalImovel}
                disabled={modoVisualizacao}
                sx={campoPesquisaSx(modoVisualizacao)}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={modoVisualizacao ? "Campo somente para visualização" : "Buscar imóvel rural cadastrado no CCIR"}>
                        <span>
                          <IconButton
                            type="button"
                            onClick={(evento) => {
                              evento.preventDefault();
                              evento.stopPropagation();
                              abrirModalImovel();
                            }}
                            disabled={modoVisualizacao}
                            sx={{
                              color: modoVisualizacao ? "#64748b" : "var(--ig-t-ffffff)",
                            }}
                          >
                            <FaSearch size={16} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                size="small"
                label="Área Total (ha)"
                value={form.areaTotalHa !== "" ? formatarArea(form.areaTotalHa) : ""}
                disabled
                helperText="Preenchida automaticamente pelo CCIR."
                sx={muiCampoSx}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) 220px",
                },
                gap: 2,
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Código do Imóvel no INCRA*"
                value={form.codigoImovelIncra}
                onChange={alterarCampo("codigoImovelIncra")}
                disabled={modoVisualizacao}
                inputProps={{ maxLength: 100 }}
                sx={muiCampoSx}
              />

              <TextField
                fullWidth
                size="small"
                label="CEP*"
                value={form.cep}
                onChange={alterarCep}
                disabled={modoVisualizacao}
                inputProps={{ maxLength: 9 }}
                sx={muiCampoSx}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Endereço*"
              value={form.endereco}
              onChange={alterarCampo("endereco")}
              disabled={modoVisualizacao}
              inputProps={{ maxLength: 500 }}
              sx={muiCampoSx}
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 1fr) 100px",
                },
                gap: 2,
                width: "100%",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label="Município*"
                value={form.municipio}
                onChange={alterarCampo("municipio")}
                disabled={modoVisualizacao}
                inputProps={{ maxLength: 255 }}
                sx={muiCampoSx}
              />

              <TextField
                fullWidth
                size="small"
                label="UF*"
                value={form.uf}
                onChange={alterarCampo("uf")}
                disabled={modoVisualizacao}
                inputProps={{ maxLength: 2 }}
                sx={muiCampoSx}
              />
            </Box>

            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Observações"
              value={form.observacao}
              onChange={alterarCampo("observacao")}
              disabled={modoVisualizacao}
              sx={muiCampoSx}
            />

            {!modoVisualizacao && arquivosPendentesCadastro.length > 0 && (
              <Box
                sx={{
                  border: "1px solid rgba(46, 168, 255, 0.45)",
                  borderRadius: "4px",
                  p: 1.5,
                  backgroundColor: "rgba(46, 168, 255, 0.06)",
                }}
              >
                <Box
                  sx={{
                    color: "var(--ig-t-90caf9)",
                    fontWeight: 700,
                    mb: 1,
                  }}
                >
                  Arquivos selecionados para envio
                </Box>

                {arquivosPendentesCadastro.map((arquivo, indice) => (
                  <Box
                    key={`${arquivo.name}-${indice}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      py: 0.5,
                      borderBottom: indice < arquivosPendentesCadastro.length - 1 ? "1px solid var(--ig-b-255-255-255-a012)" : "none",
                    }}
                  >
                    <span>{arquivo.name}</span>

                    <button type="button" className="btn-excluir-ged" onClick={() => removerArquivoCadastro(indice)} disabled={salvando}>
                      <FaTrash />
                      Remover
                    </button>
                  </Box>
                ))}
              </Box>
            )}

            <input id="uploadGEDDITR" type="file" multiple onChange={selecionarArquivosCadastro} style={{ display: "none" }} />

            <div className="iptu-botoes">
              {modoVisualizacao ? (
                <>
                  <button
                    type="button"
                    className="iptu-btn-acao btn-cadastrar"
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();
                      setModoVisualizacao(false);
                    }}
                  >
                    <FaEdit className="iptu-btn-icone" />
                    Editar
                  </button>

                  <button
                    type="button"
                    className="iptu-btn-acao btn-excluir"
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();

                      const registroAtual = registros.find((item) => Number(item.id) === Number(form.id)) || form;

                      excluirRegistro(registroAtual);
                    }}
                    disabled={excluindo}
                  >
                    <FaTrash className="iptu-btn-icone" />
                    {excluindo ? "Excluindo..." : "Excluir"}
                  </button>

                  <button type="button" className="iptu-btn-acao btn-acao" onClick={fecharFormulario}>
                    <FaTimes className="iptu-btn-icone" />
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={`iptu-btn-acao btn-ged ${
                      Number(registros.find((item) => Number(item.id) === Number(form.id))?.qtdArquivos) > 0 ? "btn-ged-ok" : ""
                    }`}
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();

                      const registroAtual = registros.find((item) => Number(item.id) === Number(form.id)) || form;

                      abrirGed(registroAtual);
                    }}
                  >
                    <FaPaperclip className="iptu-btn-icone" />
                    GED
                  </button>
                </>
              ) : editando ? (
                <>
                  <button type="submit" className="iptu-btn-acao btn-cadastrar" disabled={salvando}>
                    <FaEdit className="iptu-btn-icone" />
                    {salvando ? "Salvando..." : "Atualizar"}
                  </button>

                  <button type="button" className="iptu-btn-acao btn-acao" onClick={fecharFormulario} disabled={salvando}>
                    <FaTimes className="iptu-btn-icone" />
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={`iptu-btn-acao btn-ged ${arquivosPendentesCadastro.length > 0 ? "btn-ged-ok" : ""}`}
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();
                      document.getElementById("uploadGEDDITR")?.click();
                    }}
                    disabled={salvando}
                  >
                    <FaPaperclip className="iptu-btn-icone" />
                    GED
                    {arquivosPendentesCadastro.length > 0 && <span className="ged-badge">{arquivosPendentesCadastro.length}</span>}
                  </button>
                </>
              ) : (
                <>
                  <button type="submit" className="iptu-btn-acao btn-cadastrar" disabled={salvando}>
                    <FaSave className="iptu-btn-icone" />
                    {salvando ? "Salvando..." : "Cadastrar"}
                  </button>

                  <button type="button" className="iptu-btn-acao btn-acao" onClick={fecharFormulario} disabled={salvando}>
                    <FaTimes className="iptu-btn-icone" />
                    Cancelar
                  </button>

                  <button
                    type="button"
                    className={`iptu-btn-acao btn-ged ${arquivosPendentesCadastro.length > 0 ? "btn-ged-ok" : ""}`}
                    onClick={(evento) => {
                      evento.preventDefault();
                      evento.stopPropagation();
                      document.getElementById("uploadGEDDITR")?.click();
                    }}
                    disabled={salvando}
                  >
                    <FaPaperclip className="iptu-btn-icone" />
                    GED
                    {arquivosPendentesCadastro.length > 0 && <span className="ged-badge">{arquivosPendentesCadastro.length}</span>}
                  </button>
                </>
              )}
            </div>
          </form>
        </div>
        <Dialog
          open={modalImovelAberto}
          onClose={fecharModalImovel}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              bgcolor: "var(--ig-f-2c2c2c)",
              color: "var(--ig-t-ffffff)",
              backgroundImage: "none",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              color: "var(--ig-t-ffffff)",
              fontWeight: 700,
            }}
          >
            Selecionar Imóvel Rural do CCIR
            <IconButton type="button" onClick={fecharModalImovel} sx={{ color: "var(--ig-t-aaaaaa)" }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 2,
                pt: 1,
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={buscaImovel}
                onChange={(evento) => setBuscaImovel(evento.target.value)}
                placeholder="Pesquisar por nome do imóvel, apelido, código CCIR ou código do imóvel rural..."
                sx={muiCampoSx}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon sx={{ color: "var(--ig-t-94a3b8)" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {imoveisFiltrados.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: "var(--ig-t-d1d5db)",
                }}
              >
                Nenhum imóvel rural disponível.
              </Box>
            ) : (
              <Table size="small" sx={tabelaModalSx}>
                <TableHead>
                  <TableRow>
                    <TableCell>Código CCIR</TableCell>
                    <TableCell>Nome do Imóvel Rural</TableCell>
                    <TableCell>Apelido</TableCell>
                    <TableCell>Código do Imóvel Rural</TableCell>
                    <TableCell>Área Total (ha)</TableCell>
                    <TableCell>Município/UF</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {imoveisFiltrados.map((ccir) => (
                    <TableRow key={ccir.ccirId || ccir.id || ccir.codigoCcir} hover onClick={() => selecionarImovel(ccir)} sx={{ cursor: "pointer" }}>
                      <TableCell>{ccir.codigoCcir || "-"}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{ccir.nomeImovelRural || "-"}</TableCell>
                      <TableCell>{ccir.apelido || "-"}</TableCell>
                      <TableCell>{ccir.codigoImovelRural || "-"}</TableCell>
                      <TableCell>{formatarArea(ccir.areaTotalHa)}</TableCell>
                      <TableCell>{[ccir.municipioSedeImovelRural || ccir.municipio, ccir.uf].filter(Boolean).join("/") || "-"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={fecharModalImovel}
              sx={{
                color: "var(--ig-t-ffffff)",
                borderColor: "#64748b",
                textTransform: "none",
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={mensagem.aberta}
          autoHideDuration={5000}
          onClose={fecharMensagem}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Alert onClose={fecharMensagem} severity={mensagem.tipo} variant="filled" sx={{ width: "100%" }}>
            {mensagem.texto}
          </Alert>
        </Snackbar>
      </div>
    </ThemeProvider>
  );
}
/* =========================================================
   ESTILOS LOCAIS
========================================================= */
const menuPropsDark = {
  PaperProps: {
    sx: {
      backgroundColor: "var(--ig-f-111827)",
      color: "var(--ig-t-ffffff)",
      "& .MuiMenuItem-root": { color: "var(--ig-t-ffffff)" },
      "& .MuiMenuItem-root:hover": {
        backgroundColor: "var(--ig-f-255-255-255-a008)",
      },
      "& .Mui-selected": {
        backgroundColor: "rgba(46, 168, 255, 0.18) !important",
      },
    },
  },
};

const muiCampoSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    color: "var(--ig-t-ffffff)",
    backgroundColor: "transparent",
    "& fieldset": { borderColor: "var(--ig-b-555555)" },
    "&:hover fieldset": { borderColor: "var(--ig-b-777777)" },
    "&.Mui-focused fieldset": {
      borderColor: "#2ea8ff",
      borderWidth: 2,
    },
    "&.Mui-error fieldset": {
      borderColor: "#ef4444 !important",
      borderWidth: 2,
    },
    "&.Mui-disabled": { backgroundColor: "transparent" },
    "&.Mui-disabled .MuiInputBase-input": {
      WebkitTextFillColor: "var(--ig-t-ffffff)",
      opacity: 1,
    },
  },
  "& .MuiInputLabel-root": { color: "var(--ig-t-cbd5e1)" },
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "var(--ig-t-cbd5e1)",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#2ea8ff",
  },
  "& .MuiInputBase-input": { color: "var(--ig-t-ffffff)" },
  "& .MuiFormHelperText-root": { color: "var(--ig-t-94a3b8)" },
  "& .MuiSvgIcon-root": { color: "var(--ig-t-ffffff)" },
};

const campoPesquisaSx = (modoVisualizacao) => ({
  ...muiCampoSx,
  "& .MuiOutlinedInput-root": {
    ...muiCampoSx["& .MuiOutlinedInput-root"],
    cursor: modoVisualizacao ? "default" : "pointer",
  },
  "& .MuiInputBase-input": {
    color: "var(--ig-t-ffffff)",
    cursor: modoVisualizacao ? "default" : "pointer",
  },
});

const botaoAzulSx = {
  height: 52,
  backgroundColor: "#2196f3",
  fontWeight: 600,
  textTransform: "none",
  "&:hover": { backgroundColor: "#1976d2" },
};

const botaoOutlinedAzulSx = {
  height: 52,
  color: "#2196f3",
  borderColor: "#2196f3",
  fontWeight: 600,
  textTransform: "none",
  "&:hover": {
    borderColor: "#42a5f5",
    backgroundColor: "rgba(33, 150, 243, 0.08)",
  },
};

const paginacaoSx = {
  color: "var(--ig-t-ffffff)",
  fontSize: "0.8rem",
  "& .MuiTablePagination-selectIcon": {
    color: "var(--ig-t-ffffff)",
  },
  "& .MuiTablePagination-actions button": {
    color: "var(--ig-t-ffffff)",
  },
};

const tabelaModalSx = {
  "& .MuiTableCell-root": {
    color: "var(--ig-t-ffffff)",
    borderColor: "var(--ig-b-255-255-255-a014)",
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    backgroundColor: "var(--ig-f-1f1f1f)",
    color: "var(--ig-t-ffffff)",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: "var(--ig-f-255-255-255-a008)",
  },
};
